import { CheckBoxWithLabelWidget, FilteringList, ListItem, TabStyles, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ISearchActiveDirectoryGroupNameResult } from '~src/userManagement/groups/serverApi/requests/SearchActiveDirectoryGroupNameRequest';
import { IGroup } from '~src/userManagement/groups/serverApi/types/IGroup';

import { Messages } from '../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../commonInfra/dialogs/ReactActionDialog';
import { cssClass } from '../../style';
import { IGroupInfo } from '../groups/models/IGroupInfo';
import { GrantLicenseModes } from '../groups/serverApi/requests/SyncActiveDirectoryGroupRequest';
import { ActiveDirectoryGroups } from '../groups/services/ActiveDirectoryGroups';
import { SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { SiteRoles } from '../users/serverApi/types/ISiteRole';
import { SiteRolePickerMenu } from './siteRolePicker/SiteRolePickerMenu';


export type AddActiveDirectoryGroupDialogBodyProps = {
  readonly prompt: string;
};

export type AddActiveDirectoryGroupDialogState = {
  readonly groupModel: IGroupInfo;
  readonly siteRole: string;
  readonly grantLicenseMode: GrantLicenseModes;
};

const filterPanelStyle: React.CSSProperties = {
  height: '100%',
  maxHeight:'150px',
  width: 'auto',
  border: '1px solid #b2b2b2'
};

const filterPanelStyleWithoutBorder: React.CSSProperties = {
  height: '100%',
  maxHeight:'150px',
  width: 'auto'
};

export type PropsForConfirmActionDialogBody = ReactActionDialogBodyProps<AddActiveDirectoryGroupDialogBodyProps, AddActiveDirectoryGroupDialogState>;

export const INPUT_DEBOUNCE_MS: number = 200;
export const SEARCH_TIMEOUT_MS: number = 4000;

export function AddActiveDirectoryGroupDialogBody(props: PropsForConfirmActionDialogBody): JSX.Element {
  const [searchText, setSearchText] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [hasMoreItems, setHasMoreItems] = React.useState(false);
  const [searchTimeoutExceeded , setSearchTimeoutExceeded] = React.useState(false);

  const activeDirectoryGroups: ActiveDirectoryGroups = ActiveDirectoryGroups;

  const onSelectGroup = React.useCallback(
    (value: string) => {
      const item = items.find((item) => item.value === value);
      if (item) {
        props.updateDialogState({ ...props.dialogState, groupModel: item.groupModel });
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState, items]
  );

  const onSiteRoleSelected = React.useCallback(
    (siteRoleOption: SiteRoleOption) => {
      let grantLicenseModeChecked = (props.dialogState.grantLicenseMode === GrantLicenseModes.ON_LOGIN) && (siteRoleOption
        .role !== SiteRoles.Unlicensed);
      let grantLicenseMode = grantLicenseModeChecked ? GrantLicenseModes.ON_LOGIN : GrantLicenseModes.ON_SYNC;
      props.updateDialogState({ ...props.dialogState, siteRole: siteRoleOption.role,  grantLicenseMode});
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState, props.dialogState]
  );

  const onGroupFilterChange = React.useCallback(
    (value: string) => {
      setItems([]);
      setIsSearching(true);
      setSearchText(value);
    },
    [setItems, setIsSearching, setSearchText]
  );

  const toggleLicenseUponLogin = React.useCallback(
    (checked: boolean) => {
      let grantLicenseMode = checked ? GrantLicenseModes.ON_LOGIN : GrantLicenseModes.ON_SYNC;
      props.updateDialogState({...props.dialogState, grantLicenseMode});
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState, props.dialogState]
  );

  const getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(siteRole);
  };

  const useSearchTerm = () => {
    setSearchTimeoutExceeded(false);
    const server = getSessionManagementServiceInstance();
    const searchTermGroup: IGroup = {
      id: '',
      domainName: server.defaultDomain(),
      name: searchText,
      isAllUsersGroup: null
    };
    const groupNameBackslashIndex = searchTermGroup.name.indexOf('\\');
    if (groupNameBackslashIndex > 0) {
      searchTermGroup.domainName = searchTermGroup.name.substr(0, groupNameBackslashIndex);
      searchTermGroup.name = searchTermGroup.name.substr(groupNameBackslashIndex + 1);
    }

   const groupModel: IGroupInfo =  searchTermGroup as IGroupInfo;
   const createdItem = {
    content: createItemElement(searchTermGroup.name),
    value: 'searchTextId',
    testId: 'list-item-stop-searching',
    groupModel,
   };
   setItems([createdItem]);
  };

  React.useEffect(() => {
    let ignore = false;
    const fetchGroups = async () => {
      let groupResult: ISearchActiveDirectoryGroupNameResult;

      if (searchText.length > 0 && !ignore) {
        const triggerSearchTimeout = setTimeout(() => {
          setSearchTimeoutExceeded(true);
          setHasMoreItems(false);
        }, SEARCH_TIMEOUT_MS);
        groupResult = await activeDirectoryGroups.searchActiveDirectoryGroupName(prepareSearchTextInput(searchText));
        let newItems: ListItem[] = [];
        if(groupResult !== undefined) {
          setSearchTimeoutExceeded(false);
          clearTimeout(triggerSearchTimeout);
          if (groupResult.groups && groupResult.groups.length > 0){
            newItems = groupResult.groups.map((groupModel: IGroupInfo) => ({
              content: createItemElement(groupModel.name),
              value: '' + groupModel.id,
              testId: 'list-item-' + groupModel.id,
              groupModel,
            }));

          }
          if (!ignore) {
            setItems(newItems);
            setHasMoreItems(groupResult.hasMoreItems);
          }
          if(!newItems.find((item) => item.value === props.dialogState.groupModel?.id)){
            props.updateDialogState({ ...props.dialogState, groupModel: undefined });
          }
          if (newItems.length === 0) {
            setIsSearching(false);
          }
        }
      } else {
        setItems([]);
        setSearchTimeoutExceeded(false);
        props.updateDialogState({ ...props.dialogState, groupModel: undefined });
      }
    };


    const debouncedUpdate = _.debounce(fetchGroups, INPUT_DEBOUNCE_MS);
    debouncedUpdate();

    return () => {ignore = true;};
  },

    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
  [searchText]);


  React.useEffect(() => {
    if (items.length > 0) {
      setIsSearching(false);
    }
  }, [items]);

  /* eslint-disable jsx-a11y/no-autofocus */
  return (
    <div className="tb-add-groups-dialog">
      <div className={prompt}>
        {props.prompt}
      </div>
      <div style={ items.length > 0 ? { height: 150 } : {height: 'auto'}}>
        <FilteringList
          items={items}
          onSelect={onSelectGroup}
          searchPlaceholder={Messages.search()}
          testId="filtering-list-add-AD-group"
          style={searchText.length === 0 ? filterPanelStyleWithoutBorder : filterPanelStyle}
          onFilterChange={onGroupFilterChange}
          searchText={searchText}
          autoFocus={true}
          inputTestId="group-picker-filter"
          listTestId="group-picker-list"
        >
          {searchText.length > 0 && items.length === 0 &&
            <div className={noResults} data-tb-test-id="filtering-list-message">
              {isSearching ? Messages.addUsersImportAdGroupSearching() : Messages.noMatchingResults()}
            </div>
          }
        </FilteringList>
      </div>
      {searchTimeoutExceeded &&
        <div className="tb-add-groups-dialog-timeout-message" data-tb-test-id="add-adgroup-stop-searching">
          <TextLinkWidget
          testId="add-adGroup-stop-searching"
          onClick={useSearchTerm}>
          {Messages.addUsersImportAdGroupSearchingTimeoutExceededLink({SEARCH_TERM: searchText})}
          </TextLinkWidget>
        </div>}
      {items.length > 0 && hasMoreItems && <div className="tb-add-groups-dialog-message" data-tb-test-id="refine-search-message">
        <span>
          {Messages.addUsersImportAdGroupLimit()}
        </span>
      </div>}
      {items.length > 0 &&
              <SiteRolePickerMenu
                dropdownLabel={Messages.minimumSiteRole()}
                siteRoleOptions={SiteRoleOptionsResource.instance.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator)}
                getSiteRoleOptionBySiteRole={getSiteRoleOptionBySiteRoleCallback}
                selectedRole={SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(props.dialogState.siteRole)}
                showSiteRoleInfoLink={true}
                onOptionSelected={onSiteRoleSelected}
                style={{width: 462, marginTop: 20}}
              />}
      {items.length > 0 &&
      <CheckBoxWithLabelWidget
        label={Messages.grantLicenseUponLoginCheckboxLabel()}
        handleChange={toggleLicenseUponLogin}
        containerStyle={{marginLeft: '0px', marginTop: '16px'}}
        checked={props.dialogState.grantLicenseMode === GrantLicenseModes.ON_LOGIN}
        disabled={props.dialogState.siteRole === SiteRoles.Unlicensed}
        testId= "license-upon-login"
      />}
    </div>
  );
}

const createItemElement = (groupName: string) =>
  (<span style={{fontSize: '12px', lineHeight: '27px'}}>
    {groupName}
  </span>);

export function prepareSearchTextInput(searchText: string): string {
  // regex for exact search terms: begins & ends with ", with at least one char between.
  const exactSearchTermRegexp: RegExp = /^["].+["]$/;
  // regex for wildcard search terms: anything containing an asterisk.
  const wildSearchTermRegexp: RegExp = /.*[*]+.*/;

  // change the search text if needed, in preparation for searchActiveDirectoryGroupName.
  let result: string;
  if (exactSearchTermRegexp.test(searchText)) {
    // exact search: return the string with the " marks removed from the ends.
    result = searchText.substring(1, searchText.length-1);
  } else if (wildSearchTermRegexp.test(searchText)) {
    // wildcard search: don't change the string.
    result = searchText;
  } else {
    // fuzzy search: return the string with * prepended and appended around the group name.
    if (searchText.search(/\\/) > 0) {
      // input includes a backslash, which indicates domainName\groupName format;
      // append & prepend * to the input after the slash.
      result = searchText.replace(/([^\\]+)\\([^\\]+)/g, '$1\\*$2*');
    } else { // no backslash in input
      // append & prepend * to the text input.
      result = searchText.replace(/(.+)/g, '*$1*');
    }
  }
  return result;
}

const prompt = cssClass('add-AD-group-dialog', 'prompt', {
  fontSize: TabStyles.Typography.Sizing.Large,
  marginBottom: TabStyles.Spacing.LargeSpace,
  wordWrap: 'break-word'
});

export const noResults = cssClass('add-AD-group-dialog', 'no-results', {
  fontSize: '15px',
  marginTop: 18,
  padding: 6,
  lineHeight: '23px',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
